import apiClient from './apiClient';
import { PurchaseOrder, GoodsReceipt, AtRiskTaskResource } from './procurementService';

type Envelope<T> = { success?: boolean; data?: T; error?: string };

const unwrap = <T>(response: unknown): T => {
  const r = response as { data?: unknown; status?: number };
  const body = typeof r?.status === 'number' ? r.data : response;
  const env = body as Envelope<T>;
  return env && Object.prototype.hasOwnProperty.call(env, 'data')
    ? (env.data as T)
    : (body as T);
};

const query = (params: Record<string, unknown>) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') qs.set(key, String(value));
  });
  const value = qs.toString();
  return value ? `?${value}` : '';
};

export interface RoleTaskSourceLink {
  id: string;
  projectId: string;
  sourceType: string;
  sourceId: string;
  targetTaskType: string;
  triggerType: string;
  sourceTaskId?: string | null;
  purchaseOrderId?: string | null;
  goodsReceiptId?: string | null;
  taskResourceId?: string | null;
  metadata?: Record<string, unknown>;
  task: {
    id: string;
    taskCode: string;
    name: string;
    description?: string | null;
    taskType: string;
    lifecycleStatus: string;
    plannedStartDate?: string | null;
    priority?: string;
    createdAt?: string;
    actualEndDate?: string | null;
    completedAt?: string | null;
    taskValue?: number | string | null;
    paymentAmount?: number | string | null;
    paymentStatus?: string | null;
  };
  sourceTask?: {
    id: string;
    taskCode: string;
    name: string;
    description?: string | null;
    taskType?: string;
    lifecycleStatus?: string;
    plannedStartDate?: string | null;
    priority?: string;
    createdAt?: string;
    actualEndDate?: string | null;
    completedAt?: string | null;
    taskValue?: number | string | null;
    paymentAmount?: number | string | null;
    paymentStatus?: string | null;
    dispatchWorkers?: Array<{
      id?: string | null;
      userId?: string | null;
      name?: string | null;
      badgeCode?: string | null;
      teamId?: string | null;
      trade?: string | null;
      userEmail?: string | null;
      selectionType?: string | null;
      lifecycleStatus?: string | null;
      startedAt?: string | null;
      completedAt?: string | null;
      actualSeconds?: number | string | null;
    }>;
    completedDispatchWorkers?: Array<{
      id?: string | null;
      userId?: string | null;
      name?: string | null;
      badgeCode?: string | null;
      teamId?: string | null;
      trade?: string | null;
      userEmail?: string | null;
      selectionType?: string | null;
      lifecycleStatus?: string | null;
      startedAt?: string | null;
      completedAt?: string | null;
      actualSeconds?: number | string | null;
    }>;
    dispatchWorkersAllSubmitted?: boolean | null;
    pmIssuedToSupervisorId?: string | null;
    pmIssuedToSupervisor?: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    } | null;
    pmIssuedAt?: string | null;
    teamAssignments?: Array<{
      id: string;
      lifecycleStatus?: string;
      paymentStatus?: string | null;
      completedAt?: string | null;
      team?: {
        id: string;
        name: string;
        teamLead?: { id: string; firstName: string; lastName: string; email: string } | null;
      } | null;
    }>;
  } | null;
}

export interface ProcurementWorkspace {
  tasks: RoleTaskSourceLink[];
  atRisk: AtRiskTaskResource[];
  purchaseOrders: PurchaseOrder[];
}

export interface StoresWorkspace {
  tasks: RoleTaskSourceLink[];
  purchaseOrders: PurchaseOrder[];
  goodsReceipts: GoodsReceipt[];
  inventoryMovements: Array<{
    id: string;
    projectId?: string | null;
    resourceId: string;
    storageLocationId: string;
    taskId?: string | null;
    movementType: string;
    quantity: number;
    referenceType?: string | null;
    referenceId?: string | null;
    reasonCode?: string | null;
    notes?: string | null;
    metadata?: Record<string, unknown> | null;
    createdAt?: string;
  }>;
}

export interface AccountsTransaction {
  id: string;
  projectId: string;
  type: 'expense' | 'revenue' | 'adjustment';
  amount: number;
  transactionDate: string;
  description: string;
  status: 'draft' | 'recorded' | 'reconciled' | 'rejected';
  taskId?: string | null;
  purchaseOrderId?: string | null;
  goodsReceiptId?: string | null;
  metadata?: Record<string, unknown>;
  task?: {
    id: string;
    taskCode: string;
    name: string;
    taskType: string;
    teamAssignments?: Array<{
      id: string;
      completedAt?: string | null;
      team?: {
        id: string;
        name: string;
        teamLead?: { id: string; firstName: string; lastName: string; email: string } | null;
      } | null;
    }>;
  };
  purchaseOrder?: { id: string; poNumber: string; status: string; totalAmount: number };
  goodsReceipt?: { id: string; grnNumber: string; status: string };
}

export interface AccountsWorkspace {
  tasks: RoleTaskSourceLink[];
  transactions: AccountsTransaction[];
  purchaseOrders: PurchaseOrder[];
  approvalPurchaseOrders: PurchaseOrder[];
  closePurchaseOrders: PurchaseOrder[];
  exceptionGoodsReceipts: GoodsReceipt[];
}

class RoleWorkspaceService {
  async procurement(projectId: string): Promise<ProcurementWorkspace> {
    const response = await apiClient.get<Envelope<ProcurementWorkspace>>(`/role-workspaces/procurement${query({ project_id: projectId })}`);
    return unwrap<ProcurementWorkspace>(response);
  }

  async stores(projectId: string): Promise<StoresWorkspace> {
    const response = await apiClient.get<Envelope<StoresWorkspace>>(`/role-workspaces/stores${query({ project_id: projectId })}`);
    return unwrap<StoresWorkspace>(response);
  }

  async accounts(projectId: string): Promise<AccountsWorkspace> {
    const response = await apiClient.get<Envelope<AccountsWorkspace>>(`/role-workspaces/accounts${query({ project_id: projectId })}`);
    return unwrap<AccountsWorkspace>(response);
  }

  async completeTask(taskId: string): Promise<void> {
    await apiClient.post(`/role-workspaces/tasks/${taskId}/complete`);
  }
}

export default new RoleWorkspaceService();
